import type { DocumentTab } from 'features/knowledgeDocumentViewer/types'
import { WORKSPACE_SETTINGS_DESTINATION } from 'features/navbar/configSettings/workspaceSettingsLink'
import { NAV_ITEMS_CONFIG } from 'features/navbar/navConfig'
import { SelectedTab } from 'features/navbar/types'
import {
  CircleDollarSign,
  ClipboardCheck,
  Database,
  FileText,
  HeartPulse,
  Home,
  KeyRound,
  Puzzle,
  Settings,
  Shield,
  Sparkle,
  Upload,
  Users,
} from 'lucide-react'
import type { ComponentType } from 'react'

/** Footer / settings destinations are not in NAV_ITEMS_CONFIG. */
const FOOTER_DESTINATION_ICONS: Record<string, ComponentType> = {
  [SelectedTab.DocumentsHome]: Home,
  [SelectedTab.EntryBrowser]: Database,
  [SelectedTab.CmmsExplorer]: Database,
  [WORKSPACE_SETTINGS_DESTINATION.appConfig]: Settings,
  [WORKSPACE_SETTINGS_DESTINATION.manageUsers]: Users,
  [WORKSPACE_SETTINGS_DESTINATION.managePermissions]: KeyRound,
  [WORKSPACE_SETTINGS_DESTINATION.manageAccess]: Shield,
  [WORKSPACE_SETTINGS_DESTINATION.healthCheck]: HeartPulse,
  [WORKSPACE_SETTINGS_DESTINATION.manageExtensions]: Puzzle,
  [WORKSPACE_SETTINGS_DESTINATION.uploadDocs]: Upload,
  [WORKSPACE_SETTINGS_DESTINATION.costDashboard]: CircleDollarSign,
  [WORKSPACE_SETTINGS_DESTINATION.evaluations]: ClipboardCheck,
}

function destinationIcon(destinationKey: string): ComponentType {
  for (const [key, config] of Object.entries(NAV_ITEMS_CONFIG)) {
    if (key === destinationKey) return config.icon
  }
  return FOOTER_DESTINATION_ICONS[destinationKey] ?? Home
}

/**
 * Icon for a workspace tab. Chat tabs use Sparkle even with an empty composer
 * query (reopen-from-history clears the draft).
 */
export function getWorkspaceTabIcon(tab: DocumentTab): ComponentType {
  const item = tab.history[tab.currentIndex]

  if (item.type === 'search') {
    const isChatTab =
      item.searchState.chatOnly === true ||
      item.searchState.docChat !== undefined
    if (isChatTab) return Sparkle
    const hasSearch =
      item.searchState.query.trim().length > 0 ||
      item.searchState.browseAll === true
    if (hasSearch) {
      if (item.searchState.searchMode === 'ai') return Sparkle
      return destinationIcon(SelectedTab.Documents)
    }
    if (tab.workspaceDestination) {
      return destinationIcon(tab.workspaceDestination)
    }
    return Home
  }

  if (item.type === 'destination') {
    return destinationIcon(item.destinationKey)
  }

  if (item.type === 'document') {
    return destinationIcon(SelectedTab.Documents)
  }

  if (item.type === 'artifact') {
    return FileText
  }

  return tab.workspaceDestination
    ? destinationIcon(tab.workspaceDestination)
    : Home
}
